import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError, getToken, setToken, setUnauthorizedHandler } from './api';
import { useI18n } from './i18n';
import type { User } from './types';

interface AuthCtx {
  user: User | null;
  loading: boolean;
  /** True when the server could not be reached while checking the saved login. */
  connectionError: boolean;
  login: (email: string, password: string) => Promise<User>;
  logout: () => void;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthCtx>(null as unknown as AuthCtx);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(Boolean(getToken()));
  const [connectionError, setConnectionError] = useState(false);
  const qc = useQueryClient();
  const { setLang } = useI18n();

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    qc.clear();
  }, [qc]);

  const refresh = useCallback(async () => {
    if (!getToken()) return;
    try {
      setUser(await api.get<User>('/me'));
      setConnectionError(false);
    } catch (err) {
      // Only a rejected login logs you out. A network hiccup keeps the saved login.
      if (err instanceof ApiError && err.status === 401) logout();
      else setConnectionError(true);
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    setUnauthorizedHandler(logout);
    void refresh();
  }, [logout, refresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await api.post<{ token: string; user: User }>('/auth/login', { email, password });
      setToken(res.token);
      setUser(res.user);
      setLang(res.user.language);
      return res.user;
    },
    [setLang],
  );

  return <Ctx.Provider value={{ user, loading, connectionError, login, logout, refresh }}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);
export const isOffice = (u: User | null) => u?.role === 'owner' || u?.role === 'office';
